import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "../ui/table";
import { FaSearch, FaEdit, FaTrash, FaPhone } from "react-icons/fa";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import Switch from "../ui/form/Switch";
import { FaKey } from "react-icons/fa6";

// Custom debounce hook
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

export default function BasicTableOne({ 
  tableColumns, 
  tableRows, 
  title = "Table Data",
  handleTableAction,
  rowsPerPageOptions = [10, 25, 50, 100],
  initialRowsPerPage = 10,
  loading = false
}) {
  console.log(tableRows)
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredRows, setFilteredRows] = useState(tableRows);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  
  // Apply debounce to search term with a 300ms delay
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Reset to first page when rows change
  useEffect(() => {
    setCurrentPage(1);
  }, [tableRows, rowsPerPage]);

  // Filter rows when debounced search term changes
  useEffect(() => {
    if (!debouncedSearchTerm) {
      setFilteredRows(tableRows);
      setCurrentPage(1); // Reset to first page when search term is cleared
      return;
    }

    const filtered = tableRows.filter((row) => {
      return tableColumns.some((column) => {
        if (column.type === 'action') return false;
        const cellValue = row[column.id];
        if (cellValue === null || cellValue === undefined) return false;
        return String(cellValue).toLowerCase().includes(debouncedSearchTerm.toLowerCase());
      });
    });

    setFilteredRows(filtered);
    setCurrentPage(1); // Reset to first page when search results change
  }, [debouncedSearchTerm, tableRows, tableColumns]);

  // Handle edit action
  const handleEdit = (row) => {
    if (handleTableAction) handleTableAction('edit', row);
  };
  const handleResetPassword = (row) => {
    if (handleTableAction) handleTableAction('resetPassword', row);
  };
  // Handle delete action
  const handleDelete = (row) => {
    if (handleTableAction) handleTableAction('delete', row);
  };

  // Generic action handler for custom actions
  const handleAction = (action, row) => {
    if (handleTableAction) handleTableAction(action, row);
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredRows.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedRows = filteredRows.slice(startIndex, endIndex);
  
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setCurrentPage(1); // Reset to first page when rows per page changes
  };

  // Define column widths based on column type or specific requirements
  const getColumnWidth = (column) => {
    if (column.width) return column.width;
    
    switch(column.type) {
      case 'boolean':
        return 'w-20'; // Small width for boolean/switch columns
      case 'image':
        return 'w-20'; // Small width for image columns
      case 'action':
        return column.actions && column.actions.length > 2 ? 'w-32' : 'w-24'; // Width based on number of actions
      default:
        return ''; // Default width
    }
  };

  return (
    <div className="flex flex-col rounded-xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03] h-[550px]">
      {/* Fixed Header Section with Title and Search */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 p-3 border-b border-gray-200 dark:border-white/[0.05]">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-white">{title}</h2>
        
        <div className="relative w-full md:w-64">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <FaSearch className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          </div>
          <input
            type="text"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Table - Single Table with Fixed Header */}
      <div className="flex-grow overflow-auto ">
        <div className="min-w-full inline-block align-middle">
          <div className="overflow-hidden">
            <Table>
              {/* Fixed Header */}
              <TableHeader className="sticky top-0 z-1 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-white/[0.05]">
                <TableRow>
                  {tableColumns.map((column, index) => (
                    <TableCell
                      key={index}
                      isHeader
                      className={`px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400 ${getColumnWidth(column)}`}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHeader>
              
              {/* Table Body */}
              <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={tableColumns.length} className="text-center py-8">
                      <div className="flex flex-col items-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                        <p className="mt-3 text-gray-700 dark:text-gray-300">Loading data...</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : paginatedRows.length > 0 ? (
                  paginatedRows.map((row) => (
                    <TableRow key={row.id}>
                      {tableColumns.map((column, index) => (
                        <TableCell
                          key={index}
                          className={`px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400 ${getColumnWidth(column)}`}
                        >
                          {column.type === "boolean" ? (
                            <Switch 
                              checked={row[column.id]} 
                              size="medium"
                              color={row[column.id] ? "green" : "red"}
                              onChange={(newValue) => handleAction('toggleStatus', {...row, [column.id]: newValue})}
                              disabled={loading}
                            />
                          ) : column.type === "image" ? (
                            <img 
                              src={row[column.id]} 
                              alt={column.label} 
                              className="h-8 w-8 rounded-full object-cover"
                            />
                          ) : column.type === "action" ? (
                            <div className="flex space-x-2">
                              {column.actions && column.actions.map((action) => {
                                if(action === 'Calls'){
                                  return (
                                    <button 
                                      key="calls"
                                      onClick={() => handleAction('Calls', row)}
                                      className="p-1.5 rounded-md text-blue-600 hover:bg-blue-100 transition-colors"
                                      title="Calls"
                                    >
                                      <FaPhone className="w-6 h-6 rotate-90" />
                                    </button>
                                  );
                                }
                                 if (action === 'resetPassword') {
                                  return (
                                    <button 
                                      key="resetPassword"
                                      onClick={() => handleResetPassword(row)}
                                      className="p-1.5 rounded-md text-blue-600 hover:bg-blue-100 transition-colors"
                                      title="Reset Password"
                                    >
                                      <FaKey className="w-6 h-6" />
                                    </button>
                                  );
                                }
                                if (action === 'edit') {
                                  return (
                                    <button 
                                      key="edit"
                                      onClick={() => handleEdit(row)}
                                      className="p-1.5 rounded-md text-blue-600 hover:bg-blue-100 transition-colors"
                                      title="Edit"
                                    >
                                      <FaEdit className="w-6 h-6" />
                                    </button>
                                  );
                                }
                                if (action === 'delete') {
                                  return (
                                    <button 
                                      key="delete"
                                      onClick={() => handleDelete(row)}
                                      className="p-1.5 rounded-md text-red-600 hover:bg-red-100 transition-colors"
                                      title="Delete"
                                    >
                                      <FaTrash className="w-6 h-6" />
                                    </button>
                                  );
                                }
                                // For custom actions
                                return (
                                  <button 
                                    key={action.name}
                                    onClick={() => handleAction(action.name, row)}
                                    className={`p-1.5 rounded-md ${action.className || "text-gray-600 hover:bg-gray-100"} transition-colors`}
                                    title={action.label || action.name}
                                  >
                                    {action.icon || action.name}
                                  </button>
                                );
                              })}
                            </div>
                          ) : column.type === "custom" && typeof column.render === "function" ? (
                            column.render(row, () => handleAction(column.action || 'custom', row))
                          ) : (
                            row[column.id]
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={tableColumns.length}
                      className="px-4 py-8 text-center text-gray-500 dark:text-gray-400"
                    >
                      No results found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
      
      {/* Fixed Pagination Controls */}
      {filteredRows.length > 0 && (
        <div className="border-t border-gray-200 dark:border-white/[0.05] bg-white dark:bg-gray-900">
          <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3">
            <div className="flex items-center mb-4 sm:mb-0">
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Rows per page:
              </span>
              <select
                className="ml-2 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
              >
                {rowsPerPageOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <span className="ml-4 text-sm text-gray-700 dark:text-gray-300">
                Showing {startIndex + 1}-{Math.min(endIndex, filteredRows.length)} of {filteredRows.length}
              </span>
            </div>
            
            {/* Pagination controls */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`p-2 rounded-md ${
                  currentPage === 1
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                }`}
              >
                <FaChevronLeft className="w-3 h-3" />
              </button>
              
              <div className="flex space-x-1">
                {totalPages <= 5 ? (
                  [...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => handlePageChange(i + 1)}
                      className={`px-3 py-1 rounded-md ${
                        currentPage === i + 1
                          ? "bg-blue-600 text-white"
                          : "hover:bg-gray-100 text-gray-700 dark:text-gray-300 dark:hover:bg-gray-700"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))
                ) : (
                  <>
                    <button
                      onClick={() => handlePageChange(1)}
                      className={`px-3 py-1 rounded-md ${
                        currentPage === 1
                          ? "bg-blue-600 text-white"
                          : "hover:bg-gray-100 text-gray-700 dark:text-gray-300 dark:hover:bg-gray-700"
                      }`}
                    >
                      1
                    </button>
                    
                    {currentPage > 3 && (
                      <span className="px-3 py-1 text-gray-700 dark:text-gray-300">...</span>
                    )}
                    
                    {[...Array(5)].map((_, i) => {
                      const pageNum = currentPage - 2 + i;
                      if (pageNum > 1 && pageNum < totalPages) {
                        return (
                          <button
                            key={i}
                            onClick={() => handlePageChange(pageNum)}
                            className={`px-3 py-1 rounded-md ${
                              currentPage === pageNum
                                ? "bg-blue-600 text-white"
                                : "hover:bg-gray-100 text-gray-700 dark:text-gray-300 dark:hover:bg-gray-700"
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      }
                      return null;
                    })}
                    
                    {currentPage < totalPages - 2 && (
                      <span className="px-3 py-1 text-gray-700 dark:text-gray-300">...</span>
                    )}
                    
                    <button
                      onClick={() => handlePageChange(totalPages)}
                      className={`px-3 py-1 rounded-md ${
                        currentPage === totalPages
                          ? "bg-blue-600 text-white"
                          : "hover:bg-gray-100 text-gray-700 dark:text-gray-300 dark:hover:bg-gray-700"
                      }`}
                    >
                      {totalPages}
                    </button>
                  </>
                )}
              </div>
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`p-2 rounded-md ${
                  currentPage === totalPages
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                }`}
              >
                <FaChevronRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
